<template>
  <div
    v-if="isVisible && isAuthenticated"
    class="nav-dock lg:hidden"
  >
    <nav class="glass-pill">
      <router-link
        to="/venta"
        class="nav-item"
        :class="{ 'is-active': isActive('/venta') }"
        @click.exact="trackNavigation('venta')"
      >
        <i class="pi pi-shopping-cart"></i>
        <span>Venta</span>
      </router-link>

      <router-link
        to="/presupuesto"
        class="nav-item"
        :class="{ 'is-active': isActive('/presupuesto') }"
        @click.exact="trackNavigation('presupuesto')"
      >
        <i class="pi pi-file-edit"></i>
        <span>Presup.</span>
      </router-link>

      <router-link
        to="/gasto"
        class="nav-item"
        :class="{ 'is-active': isActive('/gasto') }"
        @click.exact="trackNavigation('gasto')"
      >
        <i class="pi pi-money-bill"></i>
        <span>Gasto</span>
      </router-link>

      <button @click="openAIChat" class="nav-item nav-item--ai">
        <i class="pi pi-sparkles"></i>
        <span>IA</span>
      </button>

      <button @click="toggleSidebar" class="nav-item">
        <i class="pi pi-bars"></i>
        <span>Menú</span>
      </button>
    </nav>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { EVENTOS, trackEvent } from '@/helpers/analytics';

const route = useRoute();
const authStore = useAuthStore();

// Control sidebar visibility from parent
const isSidebarOpen = ref(false);
const isMobile = ref(false);

// Show on mobile devices (both PWA and regular browser)
// Only if user is authenticated
const isVisible = computed(() => {
  return isMobile.value;
});

const isAuthenticated = computed(() => {
  return authStore.isAuthenticated;
});

const checkMobileView = () => {
  isMobile.value = window.innerWidth < 1024;
};

const isActive = (path) => {
  return route.path.startsWith(path);
};

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
  // Emit event to parent to toggle sidebar
  window.dispatchEvent(new CustomEvent('toggle-sidebar'));
};

const openAIChat = () => {
  // Emit event to open AI chat
  window.dispatchEvent(new CustomEvent('open-ai-chat'));
};

// Antes esto llamaba a `window.gtag` directo, que no existe: la app carga GTM,
// no gtag.js, así que el evento nunca salía. Ahora va por el mismo helper que
// el resto y aparece en el dataLayer como todos los demás.
const trackNavigation = (view) => {
  trackEvent(EVENTOS.NAV_CLICK, { nav_item: view, nav_location: 'bottom_bar' });
};

// Detect mobile view and PWA installation
const detectMobileView = () => {
  checkMobileView();
};

// Listen for sidebar toggle events from parent
const handleSidebarToggle = (event) => {
  if (event.detail?.opened !== undefined) {
    isSidebarOpen.value = event.detail.opened;
  }
};

const handleResize = () => {
  checkMobileView();
};

onMounted(() => {
  detectMobileView();
  
  // Listen for PWA install event
  window.addEventListener('beforeinstallprompt', () => {
    // PWA can be installed
  });
  
  // Listen for app installation
  window.addEventListener('appinstalled', () => {
    // PWA was installed
  });
  
  // Listen for resize to update mobile view
  window.addEventListener('resize', handleResize);
  
  // Recheck on visibility change (when app comes back to focus)
  window.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      detectMobileView();
    }
  });

  // Listen to sidebar state changes
  window.addEventListener('sidebar-state-changed', handleSidebarToggle);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('visibilitychange', detectMobileView);
  window.removeEventListener('sidebar-state-changed', handleSidebarToggle);
});
</script>

<style scoped>
/* Dock: full-width strip pinned to the bottom. It stays click-through so the
   transparent gutters beside the pill don't swallow taps on the page. */
.nav-dock {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 30;
  display: flex;
  justify-content: center;
  pointer-events: none;
  padding-bottom: max(0.75rem, env(safe-area-inset-bottom));
}

/* ── Liquid glass pill ──────────────────────────────────────────────────────
   Built in layers, the way real glass reads:
   1. backdrop-filter blur + saturate → the page genuinely showing through,
      boosted saturation so colours behind it bloom (this is what sells it).
   2. a translucent tint on top of that blur.
   3. a hairline border that is DARK in light mode — a white border over the
      near-white page (#f8fafc) is invisible, which is what made the previous
      version look flat.
   4. inset highlights: bright top edge (light hitting the curved rim) and a
      faint dark bottom edge (the glass having thickness).
   5. ::after sheen sitting above the tint but under the icons (z-index:-1
      inside the pill's own stacking context). */
.glass-pill {
  position: relative;
  isolation: isolate;
  flex: 1 1 auto;
  max-width: 30rem;
  margin: 0 0.75rem;
  display: flex;
  align-items: stretch;
  gap: 0.125rem;
  padding: 0.375rem;
  border-radius: 1.75rem;
  pointer-events: auto;

  background-color: rgba(255, 255, 255, 0.62);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  backdrop-filter: blur(24px) saturate(180%);

  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    inset 0 -1px 0 rgba(15, 23, 42, 0.04),
    0 1px 2px rgba(15, 23, 42, 0.06),
    0 10px 28px -6px rgba(15, 23, 42, 0.2);
}

.glass-pill::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.5) 0%,
    rgba(255, 255, 255, 0.08) 34%,
    rgba(255, 255, 255, 0) 52%
  );
}

.dark .glass-pill {
  background-color: rgba(30, 41, 59, 0.55);
  -webkit-backdrop-filter: blur(24px) saturate(170%) brightness(1.1);
  backdrop-filter: blur(24px) saturate(170%) brightness(1.1);
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.18),
    inset 0 -1px 0 rgba(0, 0, 0, 0.3),
    0 1px 2px rgba(0, 0, 0, 0.3),
    0 12px 32px -6px rgba(0, 0, 0, 0.55);
}

.dark .glass-pill::after {
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.16) 0%,
    rgba(255, 255, 255, 0.02) 38%,
    rgba(255, 255, 255, 0) 55%
  );
}

/* Without backdrop-filter there is no glass at all, so fall back to a solid
   surface rather than a see-through bar with unreadable labels. */
@supports not ((-webkit-backdrop-filter: blur(1px)) or (backdrop-filter: blur(1px))) {
  .glass-pill { background-color: rgba(255, 255, 255, 0.96); }
  .dark .glass-pill { background-color: rgba(30, 41, 59, 0.96); }
}

/* ── Items ──────────────────────────────────────────────────────────────── */
.nav-item {
  flex: 1 1 0;
  min-width: 0;
  min-height: 3rem; /* keeps the tap target above the 44px HIG minimum */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.5rem 0.125rem;
  border-radius: 1.375rem;
  color: var(--color-text-secondary);
  -webkit-tap-highlight-color: transparent;
  transition: color 0.25s ease, background-color 0.25s ease, transform 0.2s ease;
}

.nav-item i {
  font-size: 1.25rem;
  line-height: 1;
}

.nav-item span {
  font-size: 0.6875rem;
  line-height: 1;
  font-weight: 500;
  letter-spacing: -0.01em;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-item:active {
  transform: scale(0.92);
}

.nav-item--ai {
  color: #6366f1;
}

.dark .nav-item--ai {
  color: #a5b4fc;
}

/* Active tab: frosted indigo capsule behind the icon + label */
.nav-item.is-active {
  color: #4f46e5;
  background-color: rgba(79, 70, 229, 0.1);
  box-shadow: inset 0 0 0 1px rgba(79, 70, 229, 0.16);
}

.nav-item.is-active span {
  font-weight: 700;
}

.dark .nav-item.is-active {
  color: #a5b4fc;
  background-color: rgba(129, 140, 248, 0.16);
  box-shadow: inset 0 0 0 1px rgba(129, 140, 248, 0.22);
}

@media (prefers-reduced-motion: reduce) {
  .nav-item { transition: none; }
  .nav-item:active { transform: none; }
}
</style>
